import React, { useEffect, useMemo, useState } from "react";
import ProductTable from "../../../components/ProductTable";
import OrderPanel from "../../../components/OrderPanel";
import CashierLayout from "../Layout";
import { useProducts } from "../../../hooks/useProducts";
import type { Product } from "../../../types/store";
import type { OrderItem } from "../../../components/OrderItemRow";
import OrderConfirmation from "../../../components/OrderConfirmation";
import { useAppDispatch } from "../../../hooks";
import { useStores } from "../../../hooks/useStores";
import { extractUniqueCategories } from "../../../lib/extractUniqueCategories";
import { RefreshCcwIcon } from "lucide-react";
import { getOfflineSales } from "../../../lib/offlineSales";
import { syncOfflineSales } from "../../../slices/salesSlice";
import { fetchProducts } from "../../../slices/productSlice";
import OutOfStockModal from "../../../components/OutOfStockModal";
import RecentSalesSkeleton from "../../../components/RecentSalesTableSkeleton";


const OrdersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { products, loading } = useProducts();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [showOutOfStockModal, setShowOutOfStockModal] = useState(false);
  const [outOfStockProductName, setOutOfStockProductName] = useState("");

  // Store & Product filtering
  const { stores } = useStores();
  const [storeFilter, setStoreFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const categories = extractUniqueCategories(products);
  const [categoryFilter, setCategoryFilter] = useState("All");

  const filteredProducts = useMemo(() => {
    return products.filter(
      (p) =>
        (categoryFilter === "All" || p.category === categoryFilter) &&
        (storeFilter === "All" || p.store_id === storeFilter) &&
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [products, categoryFilter, storeFilter, searchTerm]);

  const syncOfflineSalesIfOnline = async () => {
    const offlineSales = getOfflineSales();

    if (navigator.onLine && offlineSales !== null) {
      await dispatch(syncOfflineSales());
    }
  }

  useEffect(() => {
    syncOfflineSalesIfOnline();
  }, []);

  const handleAddToCart = (product: Product) => {
    // Check if product is out of stock
    if (product.total_quantity === 0) {
      setOutOfStockProductName(product.name);
      setShowOutOfStockModal(true);
      return;
    }

    setOrderItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        if (existing.quantity < product.total_quantity) {
          return prev.map((i) =>
            i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i
          );
        }
        return prev;
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          selling_price: Number(product.selling_price),
          total_quantity: product.total_quantity,
          quantity: 1,
        },
      ];
    });
  };

  const handleQuantityChange = (id: string, qty: number) => {
    setOrderItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity: qty } : i))
    );
  };

  const handleRemove = (id: string) => {
    setOrderItems((prev) => prev.filter((i) => i.id !== id));
  };

  const handleCheckout = () => {
    if (orderItems.length === 0) return alert("No items in the order!");
    setShowConfirmation(true);
  };

  console.log("Products: ", products);
  console.log("Loading: ", loading);

  return (
    <CashierLayout>
      <div className="p-2">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3 w-full">

          {/* SEARCH + REFRESH */}
          <div className="flex items-center gap-2 w-full lg:w-auto">
            <input
              type="text"
              placeholder="Search by product name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input px-3 py-2 flex-1 lg:w-80"
            />

            <button
              onClick={() => dispatch(fetchProducts())}
              className="px-2 py-2 rounded-md bg-transparent text-gray-800 border border-gray-200"
            >
              <RefreshCcwIcon />
            </button>
          </div>

          {/* FILTERS */}
          <div className="flex flex-wrap items-end gap-3 w-full lg:w-auto">
            <div className="flex flex-col items-start">
              <span className="form-label text-xs">Filter by Category</span>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="form-input px-3 py-2 w-40"
              >
                <option value="All">All</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col items-start">
              <span className="form-label text-xs">Filter by Store</span>
              <select
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
                className="form-input px-3 py-2 w-40"
              >
                <option value="All">All Stores</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

        </div>

        <div className="flex flex-col lg:flex-row gap-6 mt-3">
          {!showConfirmation ? (
            loading ? (
              <RecentSalesSkeleton />
            ) : (
              <ProductTable
                products={filteredProducts}
                loading={loading}
                onAddToCart={handleAddToCart}
              />
            )
          ) : (
            <OrderConfirmation
              items={orderItems}
              onConfirm={() => {
                setOrderItems([]);
              }}
              onClose={() => setShowConfirmation(false)}
              storeId={storeFilter !== "All" ? storeFilter : (stores[0]?.id ?? "")}
            />
          )}
          <OrderPanel
            items={orderItems}
            onQuantityChange={handleQuantityChange}
            onRemove={handleRemove}
            onCheckout={handleCheckout}
          />
        </div>
      </div>

      <OutOfStockModal
        isOpen={showOutOfStockModal}
        onClose={() => setShowOutOfStockModal(false)}
        productName={outOfStockProductName}
      />
    </CashierLayout>
  );
};

export default OrdersPage;
